import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/**
 * Componente PWA Install Banner
 * Mostra un banner per invitare l'utente a installare l'app come PWA
 */
export function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Controlla se l'app è già installata
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');

    if (isStandalone) {
      // App già installata, non mostrare il banner
      return;
    }

    // Controlla se il prompt è disponibile
    const checkInstallPrompt = () => {
      if (window.showInstallPromotion) {
        setIsInstallable(true);
        
        // Mostra il banner dopo 30 secondi (per non essere invasivo)
        const timer = setTimeout(() => {
          // Controlla se l'utente ha già chiuso il banner in passato
          const bannerDismissed = localStorage.getItem('pwa-banner-dismissed');
          if (!bannerDismissed) {
            setShowBanner(true);
          }
        }, 30000); // 30 secondi

        return () => clearTimeout(timer);
      }
    };

    // Controlla immediatamente e poi ogni secondo per i primi 10 secondi
    checkInstallPrompt();
    const interval = setInterval(checkInstallPrompt, 1000);
    
    setTimeout(() => clearInterval(interval), 10000);

    return () => clearInterval(interval);
  }, []);

  const handleInstall = async () => {
    if (window.promptPWAInstall) {
      const accepted = await window.promptPWAInstall();
      if (accepted) {
        setShowBanner(false);
        localStorage.setItem('pwa-banner-dismissed', 'true');
      }
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Ricorda che l'utente ha chiuso il banner (per 7 giorni)
    const dismissUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-banner-dismissed', dismissUntil.toString());
  };

  // Controlla se il banner è stato chiuso di recente
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed && dismissed !== 'true') {
      const dismissUntil = parseInt(dismissed);
      if (Date.now() < dismissUntil) {
        setShowBanner(false);
      } else {
        localStorage.removeItem('pwa-banner-dismissed');
      }
    }
  }, []);

  if (!showBanner || !isInstallable) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:max-w-md">
      <Card className="border-2 border-blue-500 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 shadow-xl">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base text-gray-900 dark:text-white mb-1">
                Installa FantaClasse
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Installa l'app per un accesso più veloce e un'esperienza migliore!
              </p>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={handleInstall}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Installa
                </Button>
                <Button
                  onClick={handleDismiss}
                  size="sm"
                  variant="ghost"
                  className="text-gray-600 dark:text-gray-400"
                >
                  Non ora
                </Button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default PWAInstallBanner;
